import { useState, type ReactNode } from "react";
import { Pressable } from "react-native";
import type { Href } from "expo-router";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { Text, View } from "@src/components";
import { Icon } from "@src/components/Icon";
import { TenantLink } from "@src/features/tenants";

/** `collapsed` starts the section folded behind its title, for detail kept for debugging rather than reading. */
export function Section({
  title,
  collapsed = false,
  children,
}: {
  title: string;
  collapsed?: boolean;
  children: ReactNode;
}) {
  const [expanded, setExpanded] = useState(!collapsed);
  const { theme } = useUnistyles();
  const heading = (
    <Text textStyle="md/semibold" color="textMuted">
      {title}
    </Text>
  );

  return (
    <View style={styles.section}>
      {collapsed ?
        <Pressable
          onPress={() => setExpanded((prev) => !prev)}
          style={styles.toggle}
          accessibilityRole="button"
          accessibilityState={{ expanded }}
        >
          <Icon name={expanded ? "chevronUp" : "chevronDown"} size={16} color={theme.colors.textMuted} />
          {heading}
        </Pressable>
      : heading}
      {expanded && <View gap="md">{children}</View>}
    </View>
  );
}

/** One label/value row. A string value is selectable, so support can copy it into Patriot or a ticket. */
export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <View style={styles.field}>
      <Text textStyle="xs/regular" color="textMuted" style={styles.fieldLabel}>
        {label}
      </Text>
      <View style={styles.fieldValue}>
        {typeof children === "string" ?
          <Text textStyle="sm/regular" color="text" selectable>
            {children}
          </Text>
        : children}
      </View>
    </View>
  );
}

export function Empty({ children }: { children: string }) {
  return (
    <Text textStyle="sm/regular" color="textMuted">
      {children}
    </Text>
  );
}

export function InlineLink({ href, children }: { href: Href; children: string }) {
  return (
    // Sized to its text, so the click target ends where the underline does.
    <TenantLink href={href} style={styles.inlineLink}>
      <Text textStyle="sm/regular" style={styles.link}>
        {children}
      </Text>
    </TenantLink>
  );
}

const styles = StyleSheet.create((theme) => ({
  section: {
    backgroundColor: theme.colors.surfaceCardInner,
    borderWidth: 1,
    borderColor: theme.colors.borderCard,
    borderRadius: theme.spacing.xl,
    padding: theme.spacing["4xl"],
    gap: theme.spacing["2xl"],
  },
  toggle: { flexDirection: "row", alignItems: "center", gap: theme.spacing.xs, alignSelf: "flex-start" },
  field: { flexDirection: "row", alignItems: "baseline", gap: theme.spacing.xl },
  fieldLabel: { width: 110 },
  fieldValue: { flex: 1 },
  inlineLink: { alignSelf: "flex-start" },
  link: { color: theme.colors.interactive.buttons.secondary_foreground, textDecorationLine: "underline" },
}));
